import type { AgenciesListProps } from './agencies-list.interface';
import type { FC, ReactNode } from 'react';
import type { ConnectedProps } from 'react-redux';
import type { AgencyDto } from 'shared/dtos/agencies.dto';

import {
  closestCenter,
  DndContext,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { bindActionCreators } from '@reduxjs/toolkit';
import React from 'react';
import { connect } from 'react-redux';
import { updateAgencyThunk } from 'store/agencies/agencies.thunks';

import { usePermissions } from '../../../contexts/permissions.context';
import { vocabulary } from '../../../vocabulary/german';
import { CreateButton } from '../../buttons/create-button/create-button';
import { EntityPanel } from '../../panels/entity-panel/entity-panel';

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      updateAgencyThunk,
    },
    dispatch,
  );

const connector = connect(null, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const SortableItem = ({
  id,
  children,
  isDisabled,
}: {
  id: number;
  children: ReactNode;
  isDisabled: boolean;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: isDisabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: isDragging ? 'grabbing' : 'grab',
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
};

export const AgenciesList: FC<PropsFromRedux & AgenciesListProps> = ({
  agencies,
  selectable,
  onSelect,
  onCreate,
  assignAgencies,
  updateAgencyThunk,
}: AgenciesListProps & PropsFromRedux) => {
  const matrix = usePermissions();
  const isDraggingDisabled =
    matrix.permissions.dragFields === 'empty' ||
    matrix.permissions.dragFields === false;

  const isTouchDevice =
    typeof window !== 'undefined' && 'ontouchstart' in window;
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 8 },
  });

  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: { delay: 100, tolerance: 5 },
  });

  const sensors = useSensors(isTouchDevice ? touchSensor : pointerSensor);

  const [state, setState] = React.useState<{ agencies: AgencyDto[] }>({
    agencies: [...agencies],
  });

  const createAgencyButton = (): JSX.Element | undefined => {
    if (matrix.permissions.createNew === true) {
      return (
        <CreateButton
          text={vocabulary.buttons.createNewAgency}
          onClick={onCreate}
        />
      );
    }
  };

  const isAssign = (id: number | null) => {
    if (assignAgencies) {
      return assignAgencies.some((x: { id: number | null }) => x.id === id);
    }
  };

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = state.agencies.findIndex(
        (item) => item.id === active.id,
      );
      const newIndex = state.agencies.findIndex((item) => item.id === over.id);

      setState((state) => {
        return {
          agencies: arrayMove(state.agencies, oldIndex, newIndex),
        };
      });

      updateAgencyThunk({
        ...state.agencies[oldIndex],
        id: state.agencies[oldIndex].id,
        order: newIndex,
      });
    }
  };

  const itemsForContext = state.agencies.map((item) => item.id as number);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
    >
      <SortableContext
        items={itemsForContext}
        strategy={verticalListSortingStrategy}
      >
        <div className={'entities-list'}>
          {state.agencies.map((agency, index) => (
            <SortableItem
              key={agency.title}
              id={agency.id as number}
              isDisabled={isDraggingDisabled}
            >
              <EntityPanel
                key={index}
                isSelectable={selectable}
                onClick={() => onSelect(agency)}
                title={agency.title}
                id={agency.id}
                isDisabled={isAssign(agency.id)}
              />
            </SortableItem>
          ))}
          {onCreate && createAgencyButton()}
        </div>
      </SortableContext>
    </DndContext>
  );
};

export default connector(AgenciesList);
